import type { DemoOverlay, IDemo } from './Demo';
import type { IVariable } from './DynamicEditor';
import type { TourAspectRatio } from './ExternalPresentation';
import type { Folder } from './Folder';
import type { IntegrationMetadata } from './Integrations';
import type { RecordingTypes } from './Recording';
import type { TopAssetStatus } from './TopAssets';
import type { Tour } from './Tour';
import type { TourFrame } from './Tour';
import type { BasicUser } from './User';
import type { Workspace } from './Workspaces';

export type PlaySettings = {
  multiTab: boolean;
};

export type Playbook = {
  archiveTime: string;
  assets: PlaybookAsset[];
  createTime: string;
  creator: BasicUser;
  description: string;
  folderId: Folder['id'];
  id: string;
  isDeleted: boolean;
  name: string;
  revisionId: number;
  slugName: string;
  updateTime: string;
  workspaceId: Workspace['id'];
  image: null;
  totalPresentations?: number;
  playSettings?: PlaySettings;
  readOnly?: boolean;
};

export type PlaybookAsset = {
  assetId: string;
  assetRevisionId: number;
  assetType: PlaybookAssetType;
  autoSync: boolean;
  entity: IDemo | DemoOverlay | Tour | ExternalUrl;
};

export type PlaybookAssetType = 'demo' | 'tour' | 'externalUrl';

export type ExternalUrl = {
  name: string;
  revisionId: number;
  url: string;
  archiveTime?: string;
  createTime: string;
  creator: BasicUser;
  folderId: number;
  id: number;
  isDeleted: boolean;
  updateTime: string;
  workspaceId: number;
};

export const isExternalUrlAssetType = (
  entity: PlaybookAsset['entity']
): entity is ExternalUrl => 'url' in entity;

export const isDemoAssetType = (
  entity: PlaybookAsset['entity']
): entity is IDemo => 'demoName' in entity;

export const isTourAssetType = (
  entity: PlaybookAsset['entity']
): entity is Tour => 'tourName' in entity;

export type PlaybookPresentationMainAsset = {
  assetId: string;
  assetRevisionId: number;
  assetType: 'playbook' | 'demo';
  assetName?: string;
};

export type Play = {
  asset: PlaybookPresentationMainAsset;
  createTime: string;
  creator: BasicUser;
  folderId: Folder['id'];
  forClient: string;
  hidden: boolean;
  image: string | null;
  id: string;
  integrationMetadata: IntegrationMetadata;
  isDeleted: boolean;
  notes?: string;
  presentations: PlayPresentation[];
  slugName: string;
  updateTime: string;
  workspaceId: Workspace['id'];
  totalPresentationViews?: number;
  status?: TopAssetStatus;
};

export type DemoCreationData = {
  demoId: number;
  forClient: string;
  variables: IVariable[] | Record<string, string>;
  notes?: string;
};

export type TourCreationData = {
  tourId: number;
  forClient: string;
  notes?: string;
};

export type PlayPresentationAssets = {
  assetId: number;
  assetType: 'demo' | 'tour' | 'externalUrl';
  assetRevisionId?: number;
  creationData?: DemoCreationData | TourCreationData;
};

export type PlayPresentationSettings = {
  forClient: string;
  notes?: string;
  integrationMetadata?: IntegrationMetadata;
  hidden?: boolean;
  playLater?: boolean;
};

export type PlayCreate = {
  organizationId: string;
  workspaceId: string;
  assetType: 'demo' | 'tour' | 'playbook';
  assetId: number;
  settings: PlayPresentationSettings;
  assets: PlayPresentationAssets[];
};

export type PlayPresentation = {
  asset: PlayAssetType;
  presentationId: string;
  presentationType: PlayPresentationType;
};

type PlayPresentationTypeMap = {
  playPresentation: 'presentation';
  tourPresentation: 'tourPresentation';
  externalUrlPresentation: 'externalUrlPresentation';
};

type PlayPresentationType =
  | PlayPresentationTypeMap['playPresentation']
  | PlayPresentationTypeMap['tourPresentation']
  | PlayPresentationTypeMap['externalUrlPresentation'];

type PlayAssetType = {
  assetId: string;
  assetType: 'demo' | 'tour' | 'playbook';
  assetRevisionId: number;
  name: string;
};

type PlayDataSettings = {
  forClient: string;
};

export type PlayData = {
  presentationId: number;
  presentationType: string;
  organization: OrganizationPlayData;
  user: UserPlayData;
  presentations: PresentationItemPlayData[];
  asset: PlayAssetType;
  settings?: PlayDataSettings;
};

type OrganizationPlayData = {
  organizationId: number;
  organizationName: string;
  slugName: string;
  logo?: string;
};

type UserPlayData = {
  name: string;
  email: string;
};

export type PresentationItemPlayData =
  | {
      presentationType: PlayPresentationTypeMap['playPresentation'];
      playData: PresentationByTokenResponse;
      presentationId: number;
    }
  | {
      presentationType: PlayPresentationTypeMap['tourPresentation'];
      playData: TourPresentationByTokenResponse;
      presentationId: number;
    }
  | {
      presentationType: PlayPresentationTypeMap['externalUrlPresentation'];
      playData: ExternalUrlPresentationByTokenResponse;
      presentationId: number;
    };

type PlayByTokenResponse = {
  name: string;
  userEmail: string;
  userName: string;
  organizationId: string;
  organizationLogo?: string;
  organizationName: string;
  organizationSlug: string;
  presentationNotes?: string;
  entityType: 'presentation' | 'tourPresentation' | 'externalUrlPresentation';
};

export type PresentationByTokenResponse = PlayByTokenResponse & {
  playerToken: string;
  firstUrl: string;
  presentationId: string;
  revisionId: number;
  image?: string;
  demoId: string;
  workspaceId: string;
  recordingType: RecordingTypes;
};

export type TourPresentationByTokenResponse = PlayByTokenResponse & {
  tourPresentationId: string;
  revisionId: number;
  tour: Tour;
  frames: TourFrame[];
  tourAspectRatio: TourAspectRatio;
  tourSettings: Tour['tourSettings'];
};

export type ExternalUrlPresentationByTokenResponse = PlayByTokenResponse & {
  url: string;
  revisionId: number;
  presentationId: number;
};
